import React, { useEffect, useState } from 'react';
import { useTable, useSortBy, usePagination, useExpanded, useRowSelect } from 'react-table';
import { arrayOf, shape, bool, number, string, func } from 'prop-types';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import Loading from '../Loading';
import RecordsInfo from './RecordsInfo';
import Pagination from './Pagination';
import { ReactComponent as SortSvg } from '../../assets/icons/sort-solid.svg';
import { ReactComponent as SortDownSvg } from '../../assets/icons/sort-down-solid.svg';
import { ReactComponent as SortUpSvg } from '../../assets/icons/sort-up-solid.svg';

const sortIconStyle = css`
  width: 9px;
  height: auto;
  fill: ${({theme}) => theme.colors.information};
`;

const SortIcon = styled(SortSvg)`
  height: 14px;
  width: auto;
`;

const SortDownIcon = styled(SortDownSvg)`
  ${sortIconStyle};
`;

const SortUpIcon = styled(SortUpSvg)`
  ${sortIconStyle};
`;

const IconWrapper = styled.div`
  padding-top: 2px;
  padding-left: 5px;
`;

const Wrapper = styled.div`
  overflow: auto;
  text-align: left;
  ${({ tableStyle }) =>
    !tableStyle.removeWrapperMargin &&
    `
      margin-bottom: 2rem;
    `};

    ${Loading} {
        justify-content: center;
    }

  ${({ minWidth }) => minWidth &&
  `
    min-width: ${minWidth};
  `};
`;

const InfoWrapper = styled.div`
  text-align: center;
  padding: 3.375rem 1rem 1rem 1rem;
  margin-top: 1rem;

`;

const PaginationWrapper = styled.div`
  margin-top: 1rem;
`;

const Table = styled.table`
  width: 100%;
  overflow: auto;
`;

const Th = styled.th`
    font-size: 16px;
    font-weight: 700;
  ${({ customAlignment }) =>
    customAlignment &&
    `
    text-align: ${customAlignment};
    `};

  ${({ customHeaderWidth }) =>
    customHeaderWidth &&
    `
      width: ${customHeaderWidth}px;
      `};

  ${({ customHeaderMinWidth }) =>
    customHeaderMinWidth &&
    `
      min-width: ${customHeaderMinWidth}px;
      `};
`;

const Td = styled.td`
  ${({ customAlignment }) =>
    customAlignment &&
    `
    text-align: ${customAlignment};
    `};

  ${({ $cssMixin = '' }) => $cssMixin};
`;

const Tr = styled.tr`
  cursor: ${({rowSelectionEnabled}) => rowSelectionEnabled ? 'pointer': 'auto'};

  ${({ selected, theme }) =>
    selected &&
    `
      background-color: ${theme.colors.information_bg} !important;
    `};

  && {
    ${({ isExpanded }) =>
      isExpanded &&
      `
        border: 0;
      `};
    }
  &:hover {
    background-color: ${({rowSelectionEnabled, theme}) => rowSelectionEnabled ? theme.colors.information_bg: 'inherit'} !important;
  }
`;

const ExpandedRow = styled.tr`
  && {
    border-width: 2px;
  }
`;

const ExpandedContent = styled.td`
  padding: 0;
`;

const TableHeader = styled.thead`

  tr ${Th} {
    font-size: 14px;
    line-height: 1.5;
    padding: 0.75rem 1rem;
    white-space: normal;

    .se-table-head-content  {
      align-items: flex-start;
    }

    &.se-multicolumn-head {
      padding-bottom: 0;
      vertical-align: bottom;
      line-height: 1;

      .se-table-head-content {
        border-bottom: 1px solid ${({theme}) => theme.colors.bg_gray};
        justify-content: center;
        padding-bottom: 0.25rem;
      }
    }
  }

  tr ${Th}:first-child {
    padding-left: 0.5rem;
  }

  tr ${Th}:last-child {
    padding-right: 0.5rem;
  }
`;

const TableBody = styled.tbody`
  font-size: 12px;

  tr ${Td} {
    padding: ${({tableStyle}) => tableStyle.isDense ? '0.5rem 1rem' : '0.95rem 1rem 0.75rem'};
    vertical-align: top;
  }

  tr ${Td}:first-child {
    padding-left: 0.5rem;
  }

  tr ${Td}:last-child {
    padding-right: 0.5rem;
  }

  ${({ tableStyle }) =>
    !tableStyle.showLastRowBorder &&
    `
      tr:last-child td {
        border-bottom: none;
      }
    `};

  a,
  span[role='button'] {
  }

  /* a:hover, */
  span[role='button']:hover {
    text-decoration: underline;
  }

  span[role='button']:hover {
    cursor: pointer;
  }
`;

const TableFooter = styled.tfoot`
  font-size: 12px;
  font-weight: bold;

  tr ${Td} {
    padding: ${({tableStyle}) => tableStyle.isDense ? '0.5rem 1rem' : '0.95rem 1rem 0.75rem'};
    vertical-align: top;

    &.se-footer-hidden {
      display: none;
    }
  }

  tr ${Td}:first-child {
    padding-left: 0.5rem;
  }

  tr ${Td}:last-child {
    padding-right: 0.5rem;
  }
`;

const TableFooterInfo = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 1rem;
  align-items: flex-end;
  align-items: center;
`;

/**
 * We are using custom props (except react-table built-in props):
 * Header: customHeaderAlignment, customHeaderWidth
 * Cell: customAlignment
 * detailView: custom component which is used to display detail view of the row (row props are passed down to the component)
 *
 * Custom table style prop:
 *
 *  tableStyle: {
 *    transparentHeader: bool,
 *    removeWrapperMargin: bool,
 *    showLastRowBorder: bool,
 *    isDense: bool,
 *  }
 *
 */

const DEFAULT_TABLE_STYLE = {
  transparentHeader: false,
  removeWrapperMargin: false,
  showLastRowBorder: true,
  isDense: false,
};

const defaultPropsGetter = () => ({});

// TODO
// - export actions
// - remove !important
// - remove SEEDS classes
// - fix responsive behavior
function DataTable({
  gridId,
  columns,
  data,
  currentPageIndex,
  pageSize,
  manualMode,
  onTableStateChange,
  onPageChange,
  onRowSelect,
  selectedRowIndex,
  disableSorting,
  initialSortBy,
  loading,
  error,
  detailView,
  tableStyle,
  getCellProps = defaultPropsGetter,
  showFooter,
  showRecordsInfo,
  minWidth,
}) {

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    gotoPage,
    toggleAllRowsSelected,
    toggleRowSelected,
    state: { sortBy, pageIndex },
    rows,
    pageCount,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize,
        sortBy: initialSortBy ? [initialSortBy] : [],
      },
      disableMultiSort: true,
      manualSortBy: manualMode,
      disableSortBy: disableSorting,
      manualPagination: manualMode,
      useControlledState: (state) => {
        const newTableState = manualMode
          ? { ...state, pageIndex: currentPageIndex }
          : { ...state };
        return React.useMemo(
          () => ({
            ...newTableState,
          }),
          // TODO: fix this, and fix the next error that occurs
          // eslint-disable-next-line react-hooks/exhaustive-deps
          [state, currentPageIndex],
        );
      },
    },
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
  );

  const CustomDetailView = detailView;
  const [customTableStyle] = useState({
    ...DEFAULT_TABLE_STYLE,
    ...tableStyle,
  });

  const handleRowSelection = row => {
    toggleAllRowsSelected(false);
    row.toggleRowSelected(true)
    onRowSelect({rowId: row.id, data: row.original});
  }

  useEffect(() => {
    if(parseInt(selectedRowIndex, 10) > -1) {
      toggleAllRowsSelected(false);
      toggleRowSelected(selectedRowIndex, true);
    }
  }, [selectedRowIndex]);

  // dispatch table state in in "manual" table mode
  useEffect(() => {
    if (manualMode) {
      onTableStateChange({ sortBy });
    }
  }, [onTableStateChange, manualMode, sortBy]);

  return (
    <Wrapper id={gridId} tableStyle={customTableStyle} minWidth={minWidth}>
      <Table  {...getTableProps()} className="se-table">
        <TableHeader tableStyle={customTableStyle} className="se-table-head">
          {headerGroups.map((headerGroup) => (
            // eslint-disable-next-line
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                // eslint-disable-next-line
                <Th
                  className={
                    classNames({
                      'se-table-head--active': column.isSorted,
                      'se-multicolumn-head': column.columns?.length > 0,
                    })}
                  {...{
                    customAlignment: column.customHeaderAlignment,
                    customHeaderWidth: column.customHeaderWidth,
                    customHeaderMinWidth: column.customHeaderMinWidth,
                    ...column.getHeaderProps(column.getSortByToggleProps()),
                  }}
                >
                <div className="se-table-head-content">
                  {column.render('Header')}
                  {column.canSort && !column.isSorted && <IconWrapper><SortIcon /></IconWrapper>}
                  {column.isSorted &&
                    (column.isSortedDesc ?  <IconWrapper><SortDownIcon /></IconWrapper> : <IconWrapper><SortUpIcon /></IconWrapper>)}
                </div>
                </Th>
              ))}
            </tr>
          ))}
        </TableHeader>

        {!error && !loading && data.length > 0 && (
          <>
            <TableBody {...getTableBodyProps()} tableStyle={customTableStyle}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <React.Fragment key={row.id}>
                    {/* eslint-disable-next-line */}
                    <Tr {...row.getRowProps()}
                      className="se-table-item"
                      selected={row.isSelected}
                      isExpanded={row.isExpanded}
                      rowSelectionEnabled={!!onRowSelect}
                      onClick={() => !!onRowSelect && handleRowSelection(row)}>
                      {row.cells.map((cell) => (
                        // eslint-disable-next-line
                        <Td
                          {...cell.getCellProps([
                            { customAlignment: cell.column.customAlignment },
                            getCellProps(cell),
                          ])}
                          tableStyle={customTableStyle}
                        >
                          {cell.render('Cell')}
                        </Td>
                      ))}
                    </Tr>
                    {row.isExpanded && detailView && (
                      <ExpandedRow key={row.id}>
                        <ExpandedContent colSpan={columns.length}>
                          <CustomDetailView {...row.original} />
                        </ExpandedContent>
                      </ExpandedRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
            {
              showFooter && (
                <TableFooter tableStyle={customTableStyle}>
                  {footerGroups.map(group => (
                    <tr {...group.getFooterGroupProps()}>
                      {group.headers.map(column => (
                        <Td
                          className={
                            classNames({
                              'se-footer-hidden': column.footerHidden || column.Footer.name === 'emptyRenderer',
                            })}
                          {...column.getFooterProps([
                            { customAlignment: column.customAlignment },
                            column.getFooterProps(),
                          ])}
                          tableStyle={customTableStyle}
                        >
                          {column.render('Footer')}
                        </Td>
                      ))}
                    </tr>
                  ))}
                </TableFooter>
              )
            }
          </>
        )}
      </Table>
      {!error && !loading && data.length === 0 && (
        <InfoWrapper>No data to display.</InfoWrapper>
      )}
      {loading && (
        <InfoWrapper>
          <Loading message="Loading data..." />
        </InfoWrapper>
      )}
      {error && !loading && (
        <InfoWrapper>
          {/* <ErrorIcon /> */}
          {typeof error === 'string' ? error : 'Error: Data could not be loaded.'}
        </InfoWrapper>
      )}

      {!loading && !error && (
        <TableFooterInfo>
          {
            showRecordsInfo ? (
              <RecordsInfo
                pageIndex={pageIndex}
                pageSize={pageSize}
                totalRows={rows.length} />
            ) : <div />
          }

          {(canPreviousPage || canNextPage) && (
            <PaginationWrapper>
              <Pagination
                loading={loading}
                pageCount={pageCount}
                onPageChange={page => gotoPage(page)}
              />
            </PaginationWrapper>
          )}
        </TableFooterInfo>
      )}
    </Wrapper>
  );
}

DataTable.propTypes = {
  gridId: string,
  columns: arrayOf(shape({})).isRequired,
  data: arrayOf(shape({})).isRequired,
  getCellProps: func,
  onTableStateChange: func,
  onPageChange: func,
  onRowSelect: func,
  manualMode: bool,
  loading: bool,
  currentPageIndex: number,
  pageSize: number,
  disableSorting: bool,
  initialSortBy: shape({
    id: string.isRequired,
    desc: bool.isRequired,
  }),
  error: string,
  detailView: func,
  tableStyle: shape({
    transparentHeader: bool,
    removeWrapperMargin: bool,
    showLastRowBorder: bool,
    isDense: bool,
  }),
  selectedRowIndex: number,
  showFooter: bool,
  showRecordsInfo: bool,
  minWidth: string,
};

DataTable.defaultProps = {
  gridId: undefined,
  manualMode: false,
  loading: false,
  getCellProps: undefined,
  onTableStateChange: undefined,
  onPageChange: undefined,
  onRowSelect: undefined,
  currentPageIndex: 0,
  pageSize: 10,
  disableSorting: false,
  initialSortBy: undefined,
  detailView: undefined,
  error: undefined,
  tableStyle: {
    transparentHeader: false,
    removeWrapperMargin: false,
    showLastRowBorder: true,
    isDense: false,
  },
  selectedRowIndex: null,
  showFooter: false,
  showRecordsInfo: true,
  minWidth: undefined,
};

export default DataTable;
